/**
 * Generic assignment-module settings.
 * Override via REACT_APP_* at build time — no tenant/product-specific hardcoding.
 */

function envInt(key: string, fallback: number): number {
  const raw = process.env[key]?.trim();
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

function envStr(key: string, fallback: string): string {
  const value = process.env[key]?.trim();
  return value || fallback;
}

/** Student/guardian portal route used in redirects and parent messages. */
export const STUDENT_PORTAL_PATH = envStr('REACT_APP_STUDENT_PORTAL_PATH', '/my-school');

/** Short label for the portal (shown in UI / parent messages). */
export const STUDENT_PORTAL_LABEL = envStr('REACT_APP_STUDENT_PORTAL_LABEL', 'school portal');

/** Page size when loading a class roster for monitoring. */
export const ASSIGNMENT_ROSTER_PAGE_SIZE = envInt('REACT_APP_ASSIGNMENT_ROSTER_PAGE_SIZE', 100);

/** Safety cap on roster pages fetched (total ≈ pageSize × maxPages). */
export const ASSIGNMENT_ROSTER_MAX_PAGES = envInt('REACT_APP_ASSIGNMENT_ROSTER_MAX_PAGES', 50);

/**
 * Optional student status filter for roster loads.
 * Empty string = do not filter by status (all statuses).
 */
export const ASSIGNMENT_ROSTER_STUDENT_STATUS = envStr(
  'REACT_APP_ASSIGNMENT_ROSTER_STUDENT_STATUS',
  'active'
);

/** Max characters of assignment description included in parent notify body. */
export const ASSIGNMENT_NOTIFY_DESC_MAX = envInt('REACT_APP_ASSIGNMENT_NOTIFY_DESC_MAX', 180);

/** Who receives parent notify: guardian | student | both */
export type AssignmentNotifyPhonePreference = 'guardian' | 'student' | 'both';

export const ASSIGNMENT_NOTIFY_PHONE_PREFERENCE = ((): AssignmentNotifyPhonePreference => {
  const v = envStr('REACT_APP_ASSIGNMENT_NOTIFY_PHONE_PREF', 'guardian').toLowerCase();
  if (v === 'student' || v === 'both' || v === 'guardian') return v;
  return 'guardian';
})();

/**
 * Parent message templates. Placeholders: {{title}} {{scope}} {{due_date}} {{description}} {{portal_label}}
 * Leave a template empty to omit that line.
 */
export const ASSIGNMENT_NOTIFY_TEMPLATES = {
  headline: envStr(
    'REACT_APP_ASSIGNMENT_NOTIFY_HEADLINE',
    'New assignment for {{scope}}: {{title}}'
  ),
  dueDate: envStr('REACT_APP_ASSIGNMENT_NOTIFY_DUE', 'Due date: {{due_date}}'),
  footer: envStr(
    'REACT_APP_ASSIGNMENT_NOTIFY_FOOTER',
    'Please check the {{portal_label}} for details.'
  ),
  subject: envStr('REACT_APP_ASSIGNMENT_NOTIFY_SUBJECT', 'New assignment: {{title}}'),
  hostedTitle: envStr('REACT_APP_ASSIGNMENT_NOTIFY_HOSTED_TITLE', 'Assignment · {{title}}'),
} as const;

/** Fallback display when a class/section name is missing. */
export const ASSIGNMENT_SCOPE_FALLBACK = envStr(
  'REACT_APP_ASSIGNMENT_SCOPE_FALLBACK',
  'your child\'s class'
);

/** CSV / report column keys (API field names — stable for imports). */
export const ASSIGNMENT_REPORT_COLUMNS = [
  'admission_no',
  'student_name',
  'status',
  'submitted_at',
  'score_obtained',
  'score_max',
  'feedback',
  'submission_id',
] as const;

export type AssignmentRosterStatus = 'submitted' | 'not_submitted' | 'late';

/** Status labels for UI + CSV (overridable per status via env). */
export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentRosterStatus, string> = {
  submitted: envStr('REACT_APP_ASSIGNMENT_LABEL_SUBMITTED', 'Submitted'),
  not_submitted: envStr('REACT_APP_ASSIGNMENT_LABEL_NOT_SUBMITTED', 'Not submitted'),
  late: envStr('REACT_APP_ASSIGNMENT_LABEL_LATE', 'Submitted (late)'),
};

export const ASSIGNMENT_REPORT_SCOPE_ENTIRE_CLASS = envStr(
  'REACT_APP_ASSIGNMENT_REPORT_SCOPE_CLASS',
  'Entire class'
);

export const ASSIGNMENT_REPORT_FILENAME_PREFIX = envStr(
  'REACT_APP_ASSIGNMENT_REPORT_FILENAME_PREFIX',
  'assignment_report'
);

/** Optional default max marks prefilled on the create form (empty = no default). */
export const ASSIGNMENT_DEFAULT_MAX_MARKS = envStr(
  'REACT_APP_ASSIGNMENT_DEFAULT_MAX_MARKS',
  ''
);

/** Resolve max marks from assignment summary fields (generic across API shapes). */
export function assignmentMaxMarks(assignment: {
  score_max?: number | null;
  max_marks?: number | null;
} | null | undefined): number | null {
  if (!assignment) return null;
  const raw = assignment.score_max ?? assignment.max_marks;
  if (raw == null || Number.isNaN(Number(raw))) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

/** Apply {{key}} replacements; unknown keys left as-is. */
export function applyAssignmentTemplate(
  template: string,
  vars: Record<string, string>
): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : `{{${key}}}`
  );
}
